import { featureCollection, point } from '@turf/helpers';
import nearestPoint from '@turf/nearest-point';
import { Spin } from 'antd';
import moment, { Moment } from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isEmpty, isEqual } from 'underscore';
import Api from '../../../../../api/Api';
import { useAnalyticsSelectionStore } from '../../../../../store/analyticsSelectionStore';
import useDatasetStore from '../../../../../store/datasetStore';
import useHomeStore from '../../../../../store/homeStore';
import useLegendStore from '../../../../../store/legendStore';
import { useMarkerStore } from '../../../../../store/markerStore';
import { usePointStore } from '../../../../../store/pointStore';
import { IItemsValidation } from '../../../../../types/analytics';
import {
  ChangePointLocationParams,
  IMarker,
} from '../../../../../types/marker';
import { IEnrichedMetadata, IGetMetadata } from '../../../../../types/metadata';
import { UserGroupPermissions } from '../../../../../types/userGroupPermissions';
import { ValidationTabsEnum } from '../../../../../types/validation';
import {
  DownloadCheckboxKey,
  ROSE_PLOT_VALIDATION,
  SCATTER_PLOT_VALIDATION,
  TS_PLOT_VALIDATION,
} from '../../../../../utils/common';
import { useIsGlobalHDDtu } from '../../../../../utils/hooks';
import { isOfflineDatasetSelectionGuard } from '../../../../../utils/offlineDatasetGuard';
import styles from '../../../../features/ResultPanel/Style/ValidationPanel.module.scss';
import NoInSituData from '../InSitu/NoInsituAvailable';
import RosePlot from '../Plot/RosePlot';
import ScatterPlot from '../Plot/ScatterPlot';
import TimeSeries from '../Plot/TimeSeries';

interface Props {
  activePoint?: IMarker;
  validInSituPoint?: boolean;
  onChangePointLocation: (params: ChangePointLocationParams) => void;
  isInsituAvailable: boolean;
  activePanel: string;
  userGroupPermissions: UserGroupPermissions;
  waterDepth: string;
  cellSize: string;
  datasetId: string;
  activeInsituTab: number;
  onMeshControl: (item: string) => void;
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void;
}

const InSituTab: React.FC<Props> = ({
  activePoint,
  validInSituPoint,
  onChangePointLocation,
  cellSize,
  waterDepth,
  activeInsituTab,
  activePanel,
  userGroupPermissions,
  datasetId,
  setDatasetIsOfflineWarningVisible,
}) => {
  const [listParameters, setListParameters] = useState<IItemsValidation[]>([]);
  const [enrichedMetadata, setEnrichedMetadata] = useState<IEnrichedMetadata[]>(
    []
  );
  const markerId = activePoint?.id ?? '';
  const variable = useAnalyticsSelectionStore(
    (state) => state.getMarkerSelection(markerId).insituVariable
  );
  const setInsituVariableInStore = useAnalyticsSelectionStore(
    (state) => state.setInsituVariable
  );
  const setVariable = useCallback(
    (value: string | number) => setInsituVariableInStore(markerId, value),
    [markerId, setInsituVariableInStore]
  );
  const startDate =
    useAnalyticsSelectionStore(
      (state) => state.getMarkerSelection(markerId).insituStartDate
    ) ?? undefined;
  const setInsituStartDateInStore = useAnalyticsSelectionStore(
    (state) => state.setInsituStartDate
  );
  const setStartDate = useCallback(
    (value: Moment | undefined) =>
      setInsituStartDateInStore(markerId, value ?? null),
    [markerId, setInsituStartDateInStore]
  );
  const endDate =
    useAnalyticsSelectionStore(
      (state) => state.getMarkerSelection(markerId).insituEndDate
    ) ?? undefined;
  const setInsituEndDateInStore = useAnalyticsSelectionStore(
    (state) => state.setInsituEndDate
  );
  const setEndDate = useCallback(
    (value: Moment | undefined) =>
      setInsituEndDateInStore(markerId, value ?? null),
    [markerId, setInsituEndDateInStore]
  );
  const [loading, setLoading] = useState(false);

  const setIsChangeDatasetWarningVisible = useHomeStore(
    (state) => state.setIsChangeDatasetWarningVisible
  );
  const setTemporaryDatasetName = useHomeStore(
    (state) => state.setTemporaryDatasetName
  );
  const timeStepInterval = useHomeStore((state) => state.timeStepInterval);
  const showInSituPoints = useLegendStore((state) => state.showInSituPoints);
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const inSituPoints = usePointStore((state) => state.inSituPoints);
  const fwePoints = usePointStore((state) => state.fwePoints);
  const setInSituPoints = usePointStore((state) => state.setInSituPoints);
  const setActiveDatasetKey = useMarkerStore(
    (state) => state.setActiveDatasetKey
  );

  const prevCoordinatesRef = useRef<number[] | undefined>(undefined);
  const prevDataset = useRef<string | undefined>(undefined);
  const prevMetadata = useRef<string | undefined>(undefined);
  const isDTU = useIsGlobalHDDtu(datasetId);

  const uploadRef = useRef<any>(null);

  const onDatasetChange = (value: string) => {
    if (
      isOfflineDatasetSelectionGuard({
        datasetId: value,
        datasetBriefs,
        setDatasetIsOfflineWarningVisible,
      })
    ) {
      return;
    }

    setIsChangeDatasetWarningVisible(true);
    setTemporaryDatasetName(value);
  };

  const handleChangeCategoryKey = useCallback(
    (value: any) => {
      if (activePoint) {
        const newDatasetId =
          activePoint.downloadPage.find((e) => e.radioKey === value)
            ?.datasetId ?? activePoint.dataset;
        setActiveDatasetKey(activePoint.id, value, newDatasetId);
      }
    },
    [activePoint]
  );

  const category = useMemo(() => {
    return activePoint && !isEmpty(activePoint?.activeDatasetKey)
      ? activePoint.activeDatasetKey
      : DownloadCheckboxKey.SW;
  }, [activePoint?.activeDatasetKey]);

  const bestDataset = useMemo(() => {
    return activePoint?.downloadPage.find((dp) => dp.radioKey === category)
      ?.options;
  }, [category, activePoint]);

  const activeInSituPoint = useMemo(() => {
    if (inSituPoints && inSituPoints.length > 0 && activePoint?.coordinates) {
      const from = point(activePoint.coordinates);
      const to = inSituPoints.map((item) =>
        point(item.position.slice(0, 2), {
          source: item.source,
          name: item.name,
          startDate: item.startDate,
          endDate: item.endDate,
          type: item.type,
        })
      );
      const features = featureCollection(to);
      const nearest = nearestPoint(from, features);
      if (nearest) {
        return {
          name:
            activePoint?.insituPointName ?? (nearest.properties?.name || ''),
          coordinates: nearest.geometry.coordinates as [number, number],
          source: nearest.properties?.source,
          dateRange: [
            moment(nearest.properties.startDate),
            moment(nearest.properties.endDate),
          ] as [moment.Moment, moment.Moment],
          type: nearest.properties.type,
        };
      }
    }
    return {
      name: '',
      source: '',
      coordinates: [],
      type: '',
    };
  }, [inSituPoints, activePoint?.coordinates]);

  useEffect(() => {
    init();
  }, [
    validInSituPoint,
    userGroupPermissions,
    activePanel,
    cellSize,
    waterDepth,
  ]);

  const init = async () => {
    try {
      if (
        !activePoint ||
        !userGroupPermissions ||
        !validInSituPoint ||
        activePanel !== ValidationTabsEnum.INSITU_TAB
      )
        return;

      if (isEqual(prevMetadata.current, datasetId)) return;

      prevMetadata.current = datasetId;
      setLoading(true);
      const metadata = (await Api.metadata.getMetadata(
        datasetId
      )) as IGetMetadata;
      if (listParameters.length === 0) {
        const parametersData = await Api.insituPoint.getParametersInsitu();

        if (parametersData?.length > 0) {
          const enrichedData = parametersData.map((item: IItemsValidation) => ({
            ...item,
          }));

          enrichedData.forEach((item: IItemsValidation) => {
            const match = metadata?.ItemsInfo.find(
              (compareItem) => compareItem.ShortName === item.name
            );
            if (match) {
              item.details = match.Name;
              item.unit = match?.Unit ?? '';
            }
          });

          setListParameters(enrichedData ?? []);
        } else {
          setListParameters([]);
        }
      }
      if (
        !isEqual(prevCoordinatesRef.current, activePoint.coordinates) ||
        !isEqual(prevDataset.current, datasetId)
      ) {
        prevCoordinatesRef.current = activePoint.coordinates;
        prevDataset.current = datasetId;
        const createFullExtractionTasks =
          await Api.service.createFullExtractionTasks(
            [metadata],
            activePoint?.coordinates!,
            activePoint?.label!,
            cellSize,
            waterDepth,
            datasetBriefs,
            fwePoints,
            isDTU ? parseInt(timeStepInterval) : undefined
          );
        setEnrichedMetadata((prev) => [...prev, ...createFullExtractionTasks]);
      }
    } catch (error) {
      console.error('Error fetching parameters:', error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeMatchInsitu = () => {
    if (activePoint?.coordinates) {
      onChangePointLocation({
        coordinates: [
          Number(activeInSituPoint.coordinates[0]),
          Number(activeInSituPoint.coordinates[1]),
        ],
        insituPointName:
          activeInSituPoint.type === 'mood' ? activeInSituPoint.name : undefined,
      });
    }
  };

  const updateInSituPoints = async () => {
    try {
      setLoading(true);
      const insitu = await Api.insituPoint.getInsituPoints();
      if (insitu) {
        setInSituPoints(insitu);
      }
    } catch (error) {
      console.error('Error getting insitu point');
    } finally {
      setLoading(false);
    }
  };

  const notValidInsitu = (
    <NoInSituData
      disabledUpload={loading}
      latitude={activeInSituPoint.coordinates[0]}
      longitude={activeInSituPoint.coordinates[1]}
      name={activeInSituPoint.name}
      source={activeInSituPoint.source}
      onChangeToMatchInSitu={onChangeMatchInsitu}
      onUploadComplete={updateInSituPoints}
      uploadRef={uploadRef}
    />
  );

  return (
    <>
      <div
        style={{
          display:
            !loading && activeInsituTab === TS_PLOT_VALIDATION
              ? 'block'
              : 'none',
          height: '100%',
        }}
      >
        {
          <TimeSeries
            variable={variable}
            setVariable={setVariable}
            endDate={endDate}
            setEndDate={setEndDate}
            startDate={startDate}
            setStartDate={setStartDate}
            activeCategoryKey={category as string}
            setActiveCategory={handleChangeCategoryKey}
            listParameters={listParameters}
            validInSituPoint={validInSituPoint && showInSituPoints}
            enrichedMetadata={enrichedMetadata}
            activePoint={activePoint}
            tooltipName={{
              insitu: activeInSituPoint.name,
              point: activePoint?.label,
            }}
            activeInsituType={activeInSituPoint.type}
            dateRange={activeInSituPoint.dateRange}
            activeTab={activeInsituTab}
            activePanel={activePanel}
            datasetId={datasetId}
            userGroupPermissions={userGroupPermissions}
            bestDataset={bestDataset}
            timeStepInterval={timeStepInterval}
            datasetChange={onDatasetChange}
            notValidInsitu={notValidInsitu}
          />
        }
      </div>
      <div
        style={{
          display:
            !loading && activeInsituTab === SCATTER_PLOT_VALIDATION
              ? 'block'
              : 'none',
          height: '100%',
        }}
      >
        <ScatterPlot
          variable={variable}
          setVariable={setVariable}
          endDate={endDate}
          setEndDate={setEndDate}
          startDate={startDate}
          setStartDate={setStartDate}
          activeCategoryKey={category as string}
          setActiveCategory={handleChangeCategoryKey}
          validInSituPoint={validInSituPoint && showInSituPoints}
          enrichedMetadata={enrichedMetadata}
          activePoint={activePoint}
          insituName={activeInSituPoint.name}
          listParameters={listParameters}
          activeTab={activeInsituTab}
          dateRange={activeInSituPoint.dateRange}
          datasetId={datasetId}
          bestDataset={bestDataset}
          userGroupPermissions={userGroupPermissions}
          activeInsituType={activeInSituPoint.type}
          cellSize={cellSize}
          waterDepth={waterDepth}
          datasetChange={onDatasetChange}
          notValidInsitu={notValidInsitu}
          activePanel={activePanel}
        />
      </div>
      <div
        style={{
          display:
            !loading && activeInsituTab === ROSE_PLOT_VALIDATION
              ? 'block'
              : 'none',
          height: '100%',
        }}
      >
        <RosePlot
          activeCategoryKey={category as string}
          setActiveCategory={handleChangeCategoryKey}
          validInSituPoint={validInSituPoint && showInSituPoints}
          enrichedMetadata={enrichedMetadata}
          activePoint={activePoint}
          insituName={activeInSituPoint.name}
          listParameters={listParameters}
          dateRange={activeInSituPoint.dateRange}
          activeTab={activeInsituTab}
          datasetId={datasetId}
          userGroupPermissions={userGroupPermissions}
          bestDataset={bestDataset}
          activePanel={activePanel}
        />
      </div>
      {loading && <Spin size='large' className={styles.spinner} />}
    </>
  );
};

export default InSituTab;
